/**
 * App Store / Google Play download badges.
 *
 * These deliberately do NOT use our button styles. Apple and Google each
 * publish marketing guidelines that fix the badge's shape, colour and wording,
 * and the black pill with the store's own mark is the thing people recognise
 * without reading — a badge repainted in the SwiftOps brand is both off-guide
 * and harder to spot. Colours come from `--store-badge-*` / `--play-*` in
 * `index.css` for the same reason every other colour does: one place, no hex in
 * a component.
 *
 * Drawn rather than fetched: a strict CSP and an offline-capable app should not
 * depend on a remote PNG for a button, and inline SVG stays crisp at any size.
 *
 * The wording follows the guidelines' two-line shape, and the LEAD line is
 * translated — Apple and Google both publish localized badge artwork, and their
 * guidelines ask for the badge in the language of the surrounding content. Only
 * "App Store" and "Google Play" stay fixed: those are product names, which the
 * i18n rule keeps as-is. Translating the accessible name but not the visible
 * text would also put the two in different languages, which breaks voice
 * control and fails WCAG 2.5.3.
 */
import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/appStoreLinks';

type Store = 'ios' | 'android';

const BADGE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--store-badge-bg)',
  color: 'var(--store-badge-fg)',
  borderRadius: 8,
  padding: '8px 14px',
  textDecoration: 'none',
  lineHeight: 1.1,
  // Matches the guidelines' minimum badge height and our own tap-target floor.
  minHeight: 44,
  boxSizing: 'border-box',
};

const TOP_LINE: React.CSSProperties = {
  fontSize: 'var(--t-micro)',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  opacity: 0.85,
  display: 'block',
};

const BOTTOM_LINE: React.CSSProperties = {
  fontSize: 'var(--t-subtitle)',
  fontWeight: 600,
  letterSpacing: -0.2,
  display: 'block',
  whiteSpace: 'nowrap',
};

function AppleMark() {
  return (
    <svg width="22" height="26" viewBox="0 0 24 28" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19.3 14.9c0-3.2 2.6-4.7 2.7-4.8-1.5-2.2-3.8-2.5-4.6-2.5-1.9-.2-3.8 1.1-4.8 1.1-1 0-2.5-1.1-4.1-1.1-2.1 0-4 1.2-5.1 3.1-2.2 3.8-.6 9.4 1.5 12.5 1.1 1.5 2.3 3.2 3.9 3.1 1.6-.1 2.2-1 4.1-1s2.4 1 4.1 1c1.7 0 2.8-1.5 3.8-3 1.2-1.7 1.7-3.4 1.7-3.5-.1 0-3.2-1.3-3.2-4.9zM16.2 5.6c.9-1 1.4-2.5 1.3-3.9-1.3.1-2.8.9-3.7 1.9-.8.9-1.5 2.4-1.3 3.8 1.4.1 2.9-.7 3.7-1.8z"
      />
    </svg>
  );
}

/** Google Play's four-colour triangle. */
function PlayMark() {
  return (
    <svg width="22" height="24" viewBox="0 0 24 26" aria-hidden="true" focusable="false">
      <path fill="var(--play-blue)" d="M1.6.6C1.2 1 1 1.6 1 2.4v21.2c0 .8.2 1.4.6 1.8l.1.1 11.9-11.9v-.3L1.7.5l-.1.1z" />
      <path fill="var(--play-yellow)" d="M17.6 17.6l-4-4v-.3l4-4 .1.1 4.7 2.7c1.4.8 1.4 2.1 0 2.9l-4.7 2.6h-.1z" />
      <path fill="var(--play-red)" d="M17.7 17.5L13.6 13.4 1.6 25.4c.5.5 1.2.5 2.1.1l14-8" />
      <path fill="var(--play-green)" d="M17.7 8.5l-14-8C2.8.1 2.1.2 1.6.7l12 12 4.1-4.2z" />
    </svg>
  );
}

export default function StoreBadge({ store }: { store: Store }) {
  const { t } = useTranslation(['auth']);
  const isIos = store === 'ios';

  return (
    <a
      // Focus ring: `.sig-store-badge` in index.css. AntD's link reset gives
      // these a 1.37:1 ring, invisible against the white card they sit on.
      className="sig-store-badge"
      href={isIos ? APP_STORE_URL : PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={BADGE_STYLE}
      aria-label={t(isIos ? 'auth:invite.appStoreLink' : 'auth:invite.playStoreLink')}
    >
      {isIos ? <AppleMark /> : <PlayMark />}
      <span>
        <span style={TOP_LINE}>
          {t(isIos ? 'auth:invite.appStoreBadgeLead' : 'auth:invite.playStoreBadgeLead')}
        </span>
        {/* Product names — never translated (i18n rule 4). */}
        <span style={BOTTOM_LINE}>{isIos ? 'App Store' : 'Google Play'}</span>
      </span>
    </a>
  );
}
